/*
 * File: ApproveUsers.jsx
 * Project: lets-talk-web
 *
 * Created by Morgan Rivera on March 27, 2026 at 1:22 PM
 *
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useRef, useState
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// Services
import { approveUsers, getPendingUsers } from '../../../services/admin';


// Utilities
import { createStateLocale } from '../../../utilities/locale';
import { toastError, toastSuccess } from '../../../utilities/toaster';

// Components
import {
	Meta, Typography, AppNavigation
} from '../../../components';

// Styles
import * as S from './ApproveUsers.styles';


/**
 * Component
 */

const ApproveUsers = ({ meta, locale, data }) => {

	// Set state
	const [componentData, setComponentData] = useState(null);
	const [pageStatus, setPageStatus] = useState('idle');
	const [isLoading, setIsLoading] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [pendingUsers, setPendingUsers] = useState([]);


	// Get current locale from hook
	const clientLocale = useSelector((state) => state.locale.value);
	const stateLocale = createStateLocale(clientLocale, locale);

	// Get ui mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Create reference for components
	const isMounted = useRef(true);

	// Initialize component data function
	const fetchDataForPage = async () => {

		// Update page status
		setPageStatus('loading');

		// NV

		console.log(componentData);
		setComponentData({});
		setPageStatus('success');

	};

	// Get waitlist
	const handleWaitlist = () => {
		getPendingUsers({}).then((response) => {
			const userArray = response.data || [];
			setPendingUsers(userArray);
		});
	};

	// Handle approve users
	const handleApproveUsers = async () => {
		if (selectedUsers.length === 0) return;

		setIsLoading(true);

		approveUsers({ emails: selectedUsers }).then(() => {
			setSelectedUsers([]);
			setIsLoading(false);
			toastSuccess(uiMode, `${selectedUsers.length} User(s) Approved. Access codes sent.`);
			handleWaitlist();

		}).catch((err) => {
			setIsLoading(false);
			console.error(err);
			toastError(uiMode, 'Error. Try Again.');
		});
	};


	// Handle fetch and get user emails
	useEffect(() => {
		if (pageStatus === 'idle') {
			fetchDataForPage();
		}
	}, [pageStatus]);

	useEffect(() => {
		handleWaitlist();
	}, []);

	// Handle component initialization
	useEffect(() => {

		// Set state
		isMounted.current = true;

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Toggle a single user
	const handleSelectUser = (email) => {
		setSelectedUsers((prev) => {
			if (prev.includes(email)) {

				// Remove if already there
				return prev.filter((item) => item !== email);
			}

			// Add if not there
			return [...prev, email];

		});
	};

	// Select or Deselect All
	const handleSelectAll = () => {
		if (selectedUsers.length === pendingUsers.length && pendingUsers.length > 0) {
			setSelectedUsers([]);
		} else {
			setSelectedUsers(pendingUsers.map((user) => user.email));
		}
	};

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} locale={stateLocale} />

			{/* Component Content */}
			<AppNavigation data={data}>
				<S.Wrapper left right>

					{/* Title Widget */}
					<S.TitleWidget>
						<Typography tag="h4" weight="medium">Approve Users</Typography>
					</S.TitleWidget>

					{/* Select All Checkbox */}
					<div style={{
						display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px'
					}}
					>
						<input
							type="checkbox"
							onChange={handleSelectAll}
							checked={selectedUsers.length === pendingUsers.length && pendingUsers.length > 0}
						/>
						<Typography tag="span">
							Select All (
							{pendingUsers.length}
							{' '}
							pending)
						</Typography>
					</div>

					{/* User List */}
					<div style={{ margin: '20px 0' }}>
						{pendingUsers.map((user) => (
							<div key={user.email}>
								<input
									type="checkbox"
									checked={selectedUsers.includes(user.email)}
									onChange={() => handleSelectUser(user.email)}
								/>
								<Typography tag="span">{user.email}</Typography>
							</div>
						))}
					</div>

					{/* Action Button */}
					<S.ActionButton
						disabled={isLoading || selectedUsers.length === 0}
						isLoading={isLoading}
						onClick={handleApproveUsers}
						size="large"
					>
						Approve Selected (
						{selectedUsers.length}
						)
					</S.ActionButton>

				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

ApproveUsers.propTypes = {
	meta: PropTypes.shape(),
	locale: PropTypes.shape(),
	data: PropTypes.shape(),
};
ApproveUsers.defaultProps = {
	meta: {},
	locale: {},
	data: null
};


/**
 * Exports
 */

export default ApproveUsers;
